import Cached from '@mui/icons-material/Cached';
import Box from '@mui/material/Box';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { DEFAULT_ROW_HEIGHT } from '../../theme/constants/data-grid';
import { RESERVE_SCROLLBAR_GUTTER_ATTR } from '../../theme/constants/paper';
import {
  cleanObjectFromUndefined,
} from '../../helpers';
import { useFilteredQuery, useLastRefreshDate } from '../../hooks';
import useAvailableHeight from '../../hooks/useAvailableHeight';
import { useAdditionalQueries } from '../../hooks/useAdditionalQueries';
import useTablePagination from '../../hooks/useTablePagination';
import useUrlSyncedTableState from '../../hooks/useUrlSyncedTableState';
import { LoadingIconButton } from '../buttons';
import DataGridCheckbox from '../dataGrids/DataGridCheckbox';
import DataGridLoader from '../dataGrids/DataGridLoader';
import DataGridNoRowsOverlay from '../dataGrids/DataGridNoRowsOverlay';
import FingoStack from '../stacks/FingoStack';
import LastUpdatedDate from '../text/LastUpdatedDate';

const scrollerClass = makeStyles(() => ({
  virtualScroller: { overflowX: 'hidden', overflow: 'overlay' },
}));

const FingoMainView = ({
  id,
  query,
  additionalQueries,
  dependentAdditionalQueries,
  getDataForDependentQueryMethods,
  useUrlFilters,
  queryCustomVariables,
  queryFetchPolicy,
  onCompletedSetRows,
  onCompletedSetLength,
  hideReloadButton,
  slots: {
    header: HeaderSlot,
    actions: Actions,
    headerActions: HeaderActions,
    table: Table,
  },
  slotProps: {
    header: headerProps,
    actions: actionsProps,
    headerActions: headerActionsProps,
    table: tableProps,
  },
  skipQuery,
  fitTableToContent,
  reserveScrollbarGutter,
  isolatedRefresh,
}) => {
  const classes = scrollerClass();
  const [lastUpdated, refreshDateHandler] = useLastRefreshDate();
  const [height, refChange] = useAvailableHeight();
  const {
    globalFilter,
    setGlobalFilter,
    orderBy,
    setOrderBy,
    urlFilters,
  } = useUrlSyncedTableState({
    enabled: useUrlFilters,
    initialOrderBy: tableProps.initialOrderBy,
  });

  const paginationFiltersKey = JSON.stringify(cleanObjectFromUndefined({
    orderBy,
    globalFilter,
    ...urlFilters,
    ...queryCustomVariables,
  }));

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
  } = useTablePagination({
    initialPageSize: tableProps.initialPageSize,
    rowsPerPageOptions: tableProps.rowsPerPageOptions,
    resetKey: paginationFiltersKey,
  });

  const filterVariables = cleanObjectFromUndefined({
    orderBy,
    globalFilter,
    ...urlFilters,
    ...queryCustomVariables,
  });

  const queryVariables = {
    ...filterVariables,
    first: pageSize,
    offset: page * pageSize,
  };

  const {
    usableData: rawData,
    loading,
    deletePaginatedAndRefetch,
    deleteOwnPaginatedAndRefetch,
  } = useFilteredQuery(query, {
    variables: queryVariables,
    skip: skipQuery,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: queryFetchPolicy,
  });

  const rowCount = rawData ? onCompletedSetLength(rawData) : 0;
  const data = rawData ? onCompletedSetRows(rawData) : [];

  // Handle additional queries
  const additionalQueriesResults = useAdditionalQueries({
    additionalQueries,
    dependentAdditionalQueries,
    commonVars: queryVariables,
    data,
    getDataForDependentQueryMethods,
  });

  const refresh = useCallback(() => {
    if (isolatedRefresh) {
      deleteOwnPaginatedAndRefetch();
    } else {
      deletePaginatedAndRefetch();
    }
    Object.entries({ ...additionalQueries, ...dependentAdditionalQueries })
      .filter(([, config]) => config.refetchOnRefresh)
      .forEach(([key]) => additionalQueriesResults[key]?.refetch());
  }, [
    isolatedRefresh,
    deletePaginatedAndRefetch,
    deleteOwnPaginatedAndRefetch,
    additionalQueries,
    dependentAdditionalQueries,
    additionalQueriesResults,
  ]);

  const refetchCallback = useCallback(() => {
    refresh();
    refreshDateHandler();
  }, [refresh, refreshDateHandler]);

  const headerActions = HeaderActions && (
    <HeaderActions
      rowCount={rowCount}
      filterVariables={filterVariables}
      data={data}
      onRefresh={refresh}
      refreshLoading={loading}
      {...headerActionsProps}
    />
  );

  return (
    <>
      {HeaderSlot && (
        <HeaderSlot
          id={id}
          {...headerProps}
          headerActions={headerActions}
          finder={
            headerProps.finder && {
              onFinderChange: (newValue) => {
                setPage(0);
                setGlobalFilter(newValue);
              },
              finderValue: globalFilter,
              ...headerProps.finder,
            }
          }
        />
      )}
      {!hideReloadButton && (
        <FingoStack>
          <LoadingIconButton
            onClick={refetchCallback}
            sx={{ mr: { xs: '0', md: 'auto' } }}
            loading={loading}
            color="gray"
          >
            <Cached sx={{ display: { xs: 'none', md: 'inherit' } }} />
            <LastUpdatedDate lastUpdated={lastUpdated} />
          </LoadingIconButton>
          {Actions && <Actions {...actionsProps} data={data} />}
        </FingoStack>
      )}
      <Box
        id={`${id}-table-box`}
        ref={fitTableToContent ? null : refChange}
        {...{ [RESERVE_SCROLLBAR_GUTTER_ATTR]: reserveScrollbarGutter || undefined }}
        sx={{
          height: fitTableToContent ? 'auto' : height,
          width: '100%',
          '& .littleHeader': { fontSize: 11 },
          minHeight: DEFAULT_ROW_HEIGHT,
        }}
      >
        <Table
          rows={loading ? [] : data}
          loading={loading}
          fitTableToContent={fitTableToContent}
          loadingWithSkeleton={loading}
          additionalQueriesResults={additionalQueriesResults}
          setOrderBy={setOrderBy}
          orderBy={orderBy}
          rowCount={rowCount}
          page={page}
          pageSize={pageSize}
          rowsPerPageOptions={tableProps.rowsPerPageOptions}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          keepNonExistentRowsSelected
          scrollbarSize={0}
          rowHeight={DEFAULT_ROW_HEIGHT}
          components={{
            LoadingOverlay: DataGridLoader,
            BaseCheckbox: DataGridCheckbox,
            NoRowsOverlay: DataGridNoRowsOverlay,
          }}
          componentsProps={{ noRowsOverlay: { Message: tableProps.noRowsMessage } }}
          classes={{ virtualScroller: classes.virtualScroller }}
          {...tableProps}
        />
      </Box>
    </>
  );
};

FingoMainView.propTypes = {
  id: PropTypes.string.isRequired,
  query: PropTypes.instanceOf(Object).isRequired,
  onCompletedSetRows: PropTypes.func,
  onCompletedSetLength: PropTypes.func,
  queryCustomVariables: PropTypes.shape(),
  queryFetchPolicy: PropTypes.string,
  useUrlFilters: PropTypes.bool,
  hideReloadButton: PropTypes.bool,
  slots: PropTypes.shape({
    header: PropTypes.elementType,
    actions: PropTypes.elementType,
    headerActions: PropTypes.elementType,
    table: PropTypes.elementType.isRequired,
  }).isRequired,
  slotProps: PropTypes.shape({
    header: PropTypes.shape({
      viewTitle: PropTypes.string,
      finder: PropTypes.shape({
        searchPlaceHolder: PropTypes.string,
      }),
    }),
    actions: PropTypes.shape({}),
    headerActions: PropTypes.shape({}),
    table: PropTypes.shape({
      columns: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
      initialOrderBy: PropTypes.string,
      initialPageSize: PropTypes.number,
      noRowsMessage: PropTypes.func.isRequired,
      checkboxSelection: PropTypes.bool,
      onSelectionModelChange: PropTypes.func,
      selectionModel: PropTypes.arrayOf(PropTypes.string),
      isRowSelectable: PropTypes.func,
      keepNonExistentRowsSelected: PropTypes.bool,
      rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
    }).isRequired,
  }).isRequired,
  additionalQueries: PropTypes.objectOf(
    PropTypes.shape({
      query: PropTypes.shape({}).isRequired,
      variables: PropTypes.shape({}),
      skip: PropTypes.bool,
      queryCustomVariables: PropTypes.shape(),
      refetchOnRefresh: PropTypes.bool,
    }),
  ),
  dependentAdditionalQueries: PropTypes.objectOf(
    PropTypes.shape({
      query: PropTypes.shape({}).isRequired,
      variables: PropTypes.shape({}),
      skip: PropTypes.bool,
      queryCustomVariables: PropTypes.shape(),
      refetchOnRefresh: PropTypes.bool,
    }),
  ),
  getDataForDependentQueryMethods: PropTypes.objectOf(PropTypes.func),
  skipQuery: PropTypes.bool,
  fitTableToContent: PropTypes.bool,
  reserveScrollbarGutter: PropTypes.bool,
  isolatedRefresh: PropTypes.bool,
};

FingoMainView.defaultProps = {
  queryCustomVariables: {},
  useUrlFilters: false,
  onCompletedSetRows: (data) => Object.values(data)[0].edges.map((edge) => edge.node),
  onCompletedSetLength: (data) => Object.values(data)[0].totalCount,
  hideReloadButton: false,
  additionalQueries: {},
  dependentAdditionalQueries: {},
  getDataForDependentQueryMethods: {},
  queryFetchPolicy: 'cache-and-network',
  skipQuery: false,
  fitTableToContent: false,
  reserveScrollbarGutter: false,
  isolatedRefresh: false,
};

export default FingoMainView;
